import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
// Type-only: erased at compile time, so the lazy chunk stays split.
import type { LocationMapPageProps } from "./locationMap/locationMapPage.tsx";
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "../auth/authContext.tsx";
import { ModuleId } from "../types/session.ts";
import { NotImplementedPage } from "./notImplementedPage.tsx";
import { pageRegistry } from "./pageRegistry.ts";
import { ChunkUnavailable } from "../components/ui/chunkUnavailable.tsx";
import { notifyUpdateAvailable } from "../util/appUpdate.ts";

function LoadingSpinner() {
  return <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}><div className="loading-spinner" /></div>;
}

// Helper: lazy-load a named export
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lz = (factory: () => Promise<any>, name: string) =>
  lazy(() =>
    factory()
      .then((m: Record<string, React.ComponentType>) => ({ default: m[name]! }))
      // A chunk that 404s is almost always a stale-after-deploy chunk. Resolve to a
      // graceful placeholder (and flag the update banner) instead of rejecting, which
      // would surface the generic "Something went wrong" error boundary.
      .catch(() => { notifyUpdateAvailable(true); return { default: ChunkUnavailable }; }));

// ── Lazy page imports ──

// Settings
const SettingsPage = lz(() => import("./settings/settingsPage.tsx"), "SettingsPage");
const OperatorCommonSettingsPage = lz(() => import("./settings/operatorSettingsPage.tsx"), "OperatorCommonSettingsPage");
const OperatorPriceSettingsPage = lz(() => import("./settings/operatorSettingsPage.tsx"), "OperatorPriceSettingsPage");
const OperatorAdvancedSettingsPage = lz(() => import("./settings/operatorSettingsPage.tsx"), "OperatorAdvancedSettingsPage");

// User groups
const NewUserGroupPage = lz(() => import("./userGroups/newUserGroupPage.tsx"), "NewUserGroupPage");
const EditUserGroupPage = lz(() => import("./userGroups/editUserGroupDialog.tsx"), "EditUserGroupPage");

// Ingress
const IngressGroupPage = lz(() => import("./ingress/ingressGroupPage.tsx"), "IngressGroupPage");

// Users
const NewUserPage = lz(() => import("./users/newUserPage.tsx"), "NewUserPage");

// Operators
const OperatorPage = lz(() => import("./operators/operatorPage.tsx"), "OperatorPage");

// Email
const HandleTemplatesPage = lz(() => import("./email/handleTemplatesPage.tsx"), "HandleTemplatesPage");
const EditEmailTemplatePage = lz(() => import("./email/editEmailTemplatePage.tsx"), "EditEmailTemplatePage");

// Transactions
const ExtendDueDatePage = lz(() => import("./transactions/extendDueDatePage.tsx"), "ExtendDueDatePage");
const IgnoreTransactionsPage = lz(() => import("./transactions/ignoreTransactionsPage.tsx"), "IgnoreTransactionsPage");
const CancelReservationsPage = lz(() => import("./transactions/cancelReservationsPage.tsx"), "CancelReservationsPage");

// Profile & Misc
const EditProfilePage = lz(() => import("./profile/editProfilePage.tsx"), "EditProfilePage");
const DocumentationPage = lz(() => import("./misc/documentationPage.tsx"), "DocumentationPage");
const GridListPage = lz(() => import("./misc/gridListPage.tsx"), "GridListPage");

// InvApp
const NewWarehousePage = lz(() => import("./invapp/newWarehousePage.tsx"), "NewWarehousePage");
const NewPickupPage = lz(() => import("./invapp/newPickupPage.tsx"), "NewPickupPage");
const InvappEditOrderPage = lz(() => import("./invapp/invappEditOrderPage.tsx"), "InvappEditOrderPage");
const InvappOrderPage = lz(() => import("./invapp/invappOrderPage.tsx"), "InvappOrderPage");

// Tech
const TechUserNewPage = lz(() => import("./tech/techUserNewPage.tsx"), "TechUserNewPage");
const EditTechnicianPage = lz(() => import("./tech/editTechnicianPage.tsx"), "EditTechnicianPage");
const TicketPage = lz(() => import("./tech/ticketPage.tsx"), "TicketPage");

// Instant Visitor
const InstantVisitorPage = lz(() => import("./instantVisitor/instantVisitorPage.tsx"), "InstantVisitorPage");

// Promotions
const GenerateCodesPage = lz(() => import("./promotions/generateCodesPage.tsx"), "GenerateCodesPage");
const LovaHypePage = lz(() => import("./promotions/lovaHypePage.tsx"), "LovaHypePage");
const SearchEditPromotionPage = lz(() => import("./promotions/searchEditPromotionPage.tsx"), "SearchEditPromotionPage");
const PromotionProductListPage = lz(() => import("./promotions/promotionListPage.tsx"), "PromotionProductListPage");
const PromotionLocationListPage = lz(() => import("./promotions/promotionListPage.tsx"), "PromotionLocationListPage");

// Media & Import
const MediaUploadPage = lz(() => import("./media/mediaUploadPage.tsx"), "MediaUploadPage");
const NetworkImportPage = lz(() => import("./import/networkImportPage.tsx"), "NetworkImportPage");
const TeltonikaRouterImportPage = lz(() => import("./teltonikaRouterImport/teltonikaRouterImportPage.tsx"), "TeltonikaRouterImportPage");

// Specialized
const CbValuesPage = lz(() => import("./specialized/cbValuesPage.tsx"), "CbValuesPage");
const CardCheckPage = lz(() => import("./specialized/cardCheckPage.tsx"), "CardCheckPage");
const TopListPage = lz(() => import("./specialized/topListPage.tsx"), "TopListPage");
const OneCopyLeftPage = lz(() => import("./specialized/oneCopyLeftPage.tsx"), "OneCopyLeftPage");
const ManageDynamicColumnsPage = lz(() => import("./specialized/manageDynamicColumnsPage.tsx"), "ManageDynamicColumnsPage");
const LicensesPage = lz(() => import("./specialized/licensesPage.tsx"), "LicensesPage");
const NeoProductAttributesPage = lz(() => import("./product/neoProductAttributesPage.tsx"), "NeoProductAttributesPage");
const VehiclePage = lz(() => import("./specialized/vehiclePage.tsx"), "VehiclePage");
const AppConfigPage = lz(() => import("./specialized/appConfigPage.tsx"), "AppConfigPage");
const TransactionDetailsPage = lz(() => import("./specialized/transactionDetailsPage.tsx"), "TransactionDetailsPage");
const CustomerEventsPage = lz(() => import("./specialized/customerEventsPage.tsx"), "CustomerEventsPage");
const BlueprintDetailPage = lz(() => import("./blueprint/blueprintDetailPage.tsx"), "BlueprintDetailPage");

// Dashboard variants
const CockpitDashboardPage = lz(() => import("./cockpitDashboard/cockpitDashboardPage.tsx"), "CockpitDashboardPage");
const GlobalOpsDashboardPage = lz(() => import("./globalOps/globalOpsDashboardPage.tsx"), "GlobalOpsDashboardPage");
const InstantDashboardPage = lazy(() => import("./instantDashboard/instantDashboardPage.tsx").then(m => ({ default: m.InstantDashboardPage })));
const InventoryStartPage = lazy(() => import("./classicDashboard/inventoryStartPage.tsx").then(m => ({ default: m.InventoryStartPage })));
const IssaInsightsPage = lz(() => import("./issaInsights/issaInsightsPage.tsx"), "IssaInsightsPage");
const AgenticPromptsPage = lz(() => import("./agenticPrompts/agenticPromptsPage.tsx"), "AgenticPromptsPage");
const IssaFeedbackPage = lz(() => import("./issaFeedback/issaFeedbackPage.tsx"), "IssaFeedbackPage");

// Product
const SearchEditProductPage = lz(() => import("./product/searchEditProductPage.tsx"), "SearchEditProductPage");
const ScannableProductSearchPage = lz(() => import("./product/scannableProductSearchPage.tsx"), "ScannableProductSearchPage");
const SearchEditProductSalePage = lz(() => import("./product/searchEditProductSalePage.tsx"), "SearchEditProductSalePage");

// Package deal
const PackageDealPage = lz(() => import("./packageDeal/packageDealPage.tsx"), "PackageDealPage");
const SubsidizePage = lz(() => import("./packageDeal/subsidizePage.tsx"), "SubsidizePage");
const InteractiveExperiencePage = lz(() => import("./packageDeal/interactiveExperiencePage.tsx"), "InteractiveExperiencePage");

// Advertisement
const AdvertisementOptInPage = lz(() => import("./advertisement/advertisementOptInPage.tsx"), "AdvertisementOptInPage");
const AdvertisementPage = lz(() => import("./advertisement/advertisementPage.tsx"), "AdvertisementPage");

// Insight & Segment
const InsightDashboardPage = lz(() => import("./insightDashboard/insightDashboardPage.tsx"), "InsightDashboardPage");
const SegmentStatisticsPage = lz(() => import("./segmentStatistics/segmentStatisticsPage.tsx"), "SegmentStatisticsPage");

// Kiosk & Location
const EditKioskPage = lz(() => import("./kiosk/editKioskPage.tsx"), "EditKioskPage");
const EditLocationPage = lz(() => import("./location/editLocationPage.tsx"), "EditLocationPage");
const KioskLockedPage = lz(() => import("./kiosk/kioskLockedPage.tsx"), "KioskLockedPage");

// Merchandiser
const EditMerchandiserPage = lz(() => import("./merchandiser/editMerchandiserPage.tsx"), "EditMerchandiserPage");

// Customer
const SearchEditCustomerPage = lz(() => import("./customer/searchEditCustomerPage.tsx"), "SearchEditCustomerPage");
const EditCustomerPage = lz(() => import("./customer/editCustomerPage.tsx"), "EditCustomerPage");

// PayaP
const PayaPCorpPage = lz(() => import("./payap/payapCorpPage.tsx"), "PayaPCorpPage");
const PayaPCreditPage = lz(() => import("./payap/payapCreditPage.tsx"), "PayaPCreditPage");
const PayaPMembershipPage = lz(() => import("./payap/payapMembershipPage.tsx"), "PayaPMembershipPage");
const CloudMessagingPage = lz(() => import("./payap/cloudMessagingPage.tsx"), "CloudMessagingPage");
const PayaPMerchantPage = lz(() => import("./payap/payapMerchantPage.tsx"), "PayaPMerchantPage");

// Map & Location
// lz() types every lazy page as a propless ComponentType, which is fine for the ~100 that take
// nothing. This one takes liveSales, so the binding is narrowed rather than widening the shared
// helper and re-typing every other page.
const AnnouncementsPage = lz(() => import("./announcements/announcementsPage.tsx"), "AnnouncementsPage");
const LocationMapPage = lz(() => import("./locationMap/locationMapPage.tsx"), "LocationMapPage") as LazyExoticComponent<ComponentType<LocationMapPageProps>>;
const LiveDashboardPage = lz(() => import("./liveDashboard/liveDashboardPage.tsx"), "LiveDashboardPage");

// Price
const PriceCategoryPage = lz(() => import("./price/priceCategoryPage.tsx"), "PriceCategoryPage");
const PriceTypePage = lz(() => import("./price/priceTypePage.tsx"), "PriceTypePage");
const AdvancedPriceDefaultTaxRatesPage = lz(() => import("./price/advancedPricePage.tsx"), "AdvancedPriceDefaultTaxRatesPage");
const AdvancedPriceProductPricesPage = lz(() => import("./price/advancedPricePage.tsx"), "AdvancedPriceProductPricesPage");

// Category
const EditCategoryPage = lz(() => import("./category/editCategoryPage.tsx"), "EditCategoryPage");

// Inventory
const VisualInventoryPage = lz(() => import("./inventory/visualInventoryPage.tsx"), "VisualInventoryPage");
const EditPurchaseOrderPage = lz(() => import("./purchaseOrder/editPurchaseOrderPage.tsx"), "EditPurchaseOrderPage");

// RMA
const AddRMAPage = lz(() => import("./rma/addRMAPage.tsx"), "AddRMAPage");

// Building & UPS
const BuildingPage = lz(() => import("./building/buildingPage.tsx"), "BuildingPage");
const UpsPowerPage = lz(() => import("./ups/upsPowerPage.tsx"), "UpsPowerPage");

// Planogram
const ViewPlanogramsPage = lz(() => import("./planogram/viewPlanogramsPage.tsx"), "ViewPlanogramsPage");
const ViewPlanogramHistoryPage = lz(() => import("./planogram/viewPlanogramHistoryDialog.tsx"), "ViewPlanogramHistoryPage");
const ViewPlanogramDiffsPage = lz(() => import("./planogram/viewPlanogramDiffsDialog.tsx"), "ViewPlanogramDiffsPage");
const EditPlanogramPage = lz(() => import("./planogram/editPlanogramDialog.tsx"), "EditPlanogramPage");

// Statement config (billing config editor) + its audit log page.
const StatementConfigPage = lz(() => import("./statementConfig/statementConfigPage.tsx"), "StatementConfigPage");
const StatementAuditPage = lz(() => import("./statementConfig/statementAuditPage.tsx"), "StatementAuditPage");
const StatementDailyPage = lz(() => import("./statementDaily/statementDailyPage.tsx"), "StatementDailyPage");
const StatementAggregatePage = lz(() => import("./statementAggregate/statementAggregatePage.tsx"), "StatementAggregatePage");
const StatementBookKeepingPage = lz(() => import("./statementBookKeeping/statementBookKeepingPage.tsx"), "StatementBookKeepingPage");
const TechnicalFeePage = lz(() => import("./technicalFee/technicalFeePage.tsx"), "TechnicalFeePage");

// Replenish & Export
const ViewReplenishPage = lz(() => import("./replenish/viewReplenishPage.tsx"), "ViewReplenishPage");
const EditReplenishPage = lz(() => import("./replenish/editReplenishPage.tsx"), "EditReplenishPage");
const ScheduledExportPage = lz(() => import("./scheduledExport/scheduledExportPage.tsx"), "ScheduledExportPage");
const LocalizationDefaultPage = lz(() => import("./localization/localizationDefaultPage.tsx"), "LocalizationDefaultPage");
const LocalizationOperatorGroupPage = lz(() => import("./localization/localizationOperatorGroupPage.tsx"), "LocalizationOperatorGroupPage");
const LocalizationGroupPage = lz(() => import("./localization/localizationGroupPage.tsx"), "LocalizationGroupPage");
const KioskConfiguratorPage = lz(() => import("./kioskConfigurator/kioskConfiguratorPage.tsx"), "KioskConfiguratorPage");

// Debug / dev tools
const IconGalleryPage = lz(() => import("./debug/iconGalleryPage.tsx"), "IconGalleryPage");
const FilterGalleryPage = lz(() => import("./debug/filterGalleryPage.tsx"), "FilterGalleryPage");
const ModalGalleryPage = lz(() => import("./debug/modalGalleryPage.tsx"), "ModalGalleryPage");
const FontGalleryPage = lz(() => import("./debug/fontGalleryPage.tsx"), "FontGalleryPage");
const DateGalleryPage = lz(() => import("./debug/dateGalleryPage.tsx"), "DateGalleryPage");

// SuperAdmin tools (Page Management + audit log viewer). Both are gated by
// isSuperAdmin inside the page component; URL access by a non-SA user redirects.
const PageMgmtPage = lz(() => import("./superAdmin/pageMgmtPage.tsx"), "PageMgmtPage");
const PageMgmtAuditPage = lz(() => import("./superAdmin/pageMgmtAuditPage.tsx"), "PageMgmtAuditPage");
const PageMgmtDocsPage = lz(() => import("./superAdmin/pageMgmtDocsPage.tsx"), "PageMgmtDocsPage");
const OffloadZonesPage = lz(() => import("./offloadZones/offloadZonesPage.tsx"), "OffloadZonesPage");

// Platform invoicing. Accounts and coverage gate on isSuperAdmin inside the page; the invoice
// list is a tenant page and the server scopes it to the active endpoint.
const BillingAccountsPage = lz(() => import("./platformBilling/billingAccountsPage.tsx"), "BillingAccountsPage");
const BillingCoveragePage = lz(() => import("./platformBilling/billingCoveragePage.tsx"), "BillingCoveragePage");
const BillingInvoicesPage = lz(() => import("./platformBilling/billingInvoicesPage.tsx"), "BillingInvoicesPage");

// Generic page components (explicit lazy for props preservation)
const GenericGridPage = lazy(() => import("./generic/genericGridPage.tsx").then(m => ({ default: m.GenericGridPage })));
const GenericSearchEditPage = lazy(() => import("./generic/genericSearchEditPage.tsx").then(m => ({ default: m.GenericSearchEditPage })));
const GenericTabbedPage = lazy(() => import("./generic/genericTabbedPage.tsx").then(m => ({ default: m.GenericTabbedPage })));

// ── Route resolution ──

function getExplicitRoute(pageName: string): React.ReactElement | null {
  switch (pageName) {
    case "icongallery": return <IconGalleryPage />;
    case "filtergallery": return <FilterGalleryPage />;
    case "modalgallery": return <ModalGalleryPage />;
    case "fontgallery": return <FontGalleryPage />;
    case "dategallery": return <DateGalleryPage />;
    case "pagemgmt": return <PageMgmtPage />;
    case "pagemgmtaudit": return <PageMgmtAuditPage />;
    case "pagemgmtdocs": return <PageMgmtDocsPage />;
    case "offloadzones": return <OffloadZonesPage />;
    case "billingaccounts": return <BillingAccountsPage />;
    case "billingcoverage": return <BillingCoveragePage />;
    case "billinginvoice": return <BillingInvoicesPage />;
    case "settings": return <SettingsPage />;
    case "operatorcommonsettings": return <OperatorCommonSettingsPage />;
    case "operatorpricesettings": return <OperatorPriceSettingsPage />;
    case "operatoradvsettings": return <OperatorAdvancedSettingsPage />;
    case "newusergroup": return <NewUserGroupPage />;
    case "editusergroup": return <EditUserGroupPage />;
    case "ingressgroup": return <IngressGroupPage />;
    case "newuser": return <NewUserPage />;
    case "operator": return <OperatorPage />;
    case "handletemplates": return <HandleTemplatesPage />;
    case "editemailtemplate": return <EditEmailTemplatePage />;
    case "extendduedate": return <ExtendDueDatePage />;
    case "ignoretransactions": return <IgnoreTransactionsPage />;
    case "cancelreservations": return <CancelReservationsPage />;
    case "editprofile": return <EditProfilePage />;
    case "documentation": return <DocumentationPage />;
    case "gridlist": return <GridListPage />;
    case "newwarehouse": return <NewWarehousePage />;
    case "newpickup": return <NewPickupPage />;
    case "techusernew": return <TechUserNewPage />;
    case "generatecodes": return <GenerateCodesPage />;
    case "instantvisitor": return <InstantVisitorPage />;
    case "mediaupload": return <MediaUploadPage />;
    case "networkimport": return <NetworkImportPage />;
    case "teltonikarouterimport": return <TeltonikaRouterImportPage />;
    case "cbvaluesmanagement": return <CbValuesPage />;
    case "cardcheckpage": return <CardCheckPage />;
    case "toplist": return <TopListPage />;
    case "onecopyleft": return <OneCopyLeftPage />;
    case "managedynamiccolumns": return <ManageDynamicColumnsPage />;
    case "licensepage": return <LicensesPage />;
    case "neoproductattributes": return <NeoProductAttributesPage />;
    case "vehiclepage": return <VehiclePage />;
    case "startpage": return <Navigate to="/" replace />;
    case "appconfigpage": return <AppConfigPage />;
    case "transactiondetails": return <TransactionDetailsPage />;
    case "editcustomerevents": return <CustomerEventsPage />;
    case "blueprintdetail": return <BlueprintDetailPage />;
    case "searcheditproduct": return <SearchEditProductPage />;
    case "cockpitdashboard": return <CockpitDashboardPage />;
    case "globalopsdashboard": return <GlobalOpsDashboardPage />;
    case "issainsights": return <IssaInsightsPage />;
    case "agenticprompts": return <AgenticPromptsPage />;
    case "issafeedback": return <IssaFeedbackPage />;
    case "packagedeal": return <PackageDealPage />;
    case "subsidize": return <SubsidizePage />;
    case "interactiveexperiencesearch": return <InteractiveExperiencePage />;
    case "advertisementoptin": return <AdvertisementOptInPage />;
    case "advertisement": return <AdvertisementPage />;
    case "insightdashboard": return <InsightDashboardPage />;
    case "segmentstatistics":
    case "kiosksegmentsdashboard": return <SegmentStatisticsPage />;
    case "editkiosk": return <EditKioskPage />;
    case "editlocation": return <EditLocationPage />;
    case "editmerchandiser": return <EditMerchandiserPage />;
    case "lovahype": return <LovaHypePage />;
    case "searcheditpromotion": return <SearchEditPromotionPage />;
    case "promotionproductlist": return <PromotionProductListPage />;
    case "promotionlocationlist": return <PromotionLocationListPage />;
    case "searcheditcustomer": return <SearchEditCustomerPage />;
    case "editcustomer": return <EditCustomerPage />;
    case "payapcorp": return <PayaPCorpPage />;
    case "payapcredit": return <PayaPCreditPage />;
    case "payapmembership": return <PayaPMembershipPage />;
    case "editcloudmessage": return <CloudMessagingPage />;
    case "merchanteditpage":
    case "payapmerchantedit": return <PayaPMerchantPage />;
    // Two routes, one component, deliberately different: the quiet map stays as it always was,
    // and Admin Map is the one where the money is live. Without liveSales={false} here the
    // permission alone would decide and a holder would see revenue on both.
    case "locationmap": return <LocationMapPage liveSales={false} />;
    case "adminmap": return <LocationMapPage />;
    case "announcements": return <AnnouncementsPage />;
    case "livedashboard": return <LiveDashboardPage />;
    case "kiosklocked": return <KioskLockedPage />;
    case "pricecategory": return <PriceCategoryPage />;
    case "pricetype": return <PriceTypePage />;
    case "advancedpricedefaulttaxrates": return <AdvancedPriceDefaultTaxRatesPage />;
    case "advancedpriceproductprices": return <AdvancedPriceProductPricesPage />;
    case "editcategory": return <EditCategoryPage />;
    case "visualinventory": return <VisualInventoryPage />;
    case "editpurchaseorder": return <EditPurchaseOrderPage />;
    case "addrma": return <AddRMAPage />;
    case "scannableproductsearch": return <ScannableProductSearchPage />;
    case "searcheditproductsale": return <SearchEditProductSalePage />;
    case "buildingpage": return <BuildingPage />;
    case "upspowerpage": return <UpsPowerPage />;
    case "invappeditorder": return <InvappEditOrderPage />;
    case "invapporder": return <InvappOrderPage />;
    case "viewplanograms": return <ViewPlanogramsPage />;
    case "viewplanogramhistory": return <ViewPlanogramHistoryPage />;
    case "viewplanogramdiffs": return <ViewPlanogramDiffsPage />;
    case "editplanogram": return <EditPlanogramPage />;
    case "viewreplenish": return <ViewReplenishPage />;
    case "editreplenish": return <EditReplenishPage />;
    case "scheduledexport": return <ScheduledExportPage />;
    case "localizationdefault": return <LocalizationDefaultPage />;
    case "localizationoperatorgroup": return <LocalizationOperatorGroupPage />;
    case "localizationgroup": return <LocalizationGroupPage />;
    case "kioskconfigurator": return <KioskConfiguratorPage />;
    case "edittechnician": return <EditTechnicianPage />;
    case "ticketpage": return <TicketPage />;
    case "statementmgmt": return <StatementConfigPage />;
    case "statementmgmtaudit": return <StatementAuditPage />;
    case "statementdaily": return <StatementDailyPage />;
    case "statementaggregate": return <StatementAggregatePage />;
    case "statementbookkeeping": return <StatementBookKeepingPage />;
    case "technicalfee": return <TechnicalFeePage />;
    default: return null;
  }
}

export function CustomPageRoute() {
  const { name } = useParams<{ name: string }>();
  const { activeEndpoint } = useAuth();
  const pageName = (name ?? "").toLowerCase();

  // Module-level startpage override: all dashboard routes redirect to module startpage
  if ((pageName === "" || pageName === "startpage" || pageName === "cockpitdashboard") && activeEndpoint?.ModuleID === ModuleId.InstantInventory) {
    return <Suspense fallback={<LoadingSpinner />}><InventoryStartPage /></Suspense>;
  }

  const explicit = getExplicitRoute(pageName);
  if (explicit) return <Suspense fallback={<LoadingSpinner />}>{explicit}</Suspense>;

  const entry = pageRegistry[pageName];
  if (!entry || entry.kind !== "customPage") return <NotImplementedPage />;

  const impl = entry.implementation;
  if (!impl) return <NotImplementedPage />;

  const hasEdit = impl.apiMethods?.includes("UpdateGridCell") ?? false;
  const hasActions = impl.apiMethods?.includes("WebExecuteSP") ?? false;

  let content: React.ReactElement;
  switch (impl.uiPattern) {
    case "basePage": {
      if (pageName.startsWith("instantdashboard")) {
        content = <InstantDashboardPage pageName={pageName} />;
        break;
      }
      const gridName = impl.primaryGridName ?? pageName;
      content = <GenericGridPage gridName={gridName} pageName={pageName} editable={hasEdit} hasActions={hasActions} />;
      break;
    }
    case "searchEdit":
      content = <GenericSearchEditPage pageName={pageName} />;
      break;
    case "tabControl":
      content = <GenericTabbedPage pageName={pageName} />;
      break;
    case "searchEditTab":
      content = <GenericTabbedPage pageName={pageName} searchEdit />;
      break;
    default:
      return <NotImplementedPage />;
  }

  return <Suspense fallback={<LoadingSpinner />}>{content}</Suspense>;
}
